import React, { memo } from "react";
import { useTranslate } from "helpers";
import { FleetTireAddRow } from "services/api/fleet/tires/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TireGroup, getGroupLabel, getTireLabel } from "../helpers";
import TireGroupHeader from "./TireGroupHeader";
import TireReadOnlyRow from "./TireReadOnlyRow";

interface TireTableProps {
    tires: FleetTireAddRow[];
    groups: TireGroup[];
    allSameDimensions: boolean;
    dirtyFields: Set<string>;
    tireTypeOptions: DropdownOption[];
    manufacturerOptions: DropdownOption[];
    onGroupChange: (indices: number[], field: string, value: any) => void;
    onDeleteClick: (event: React.MouseEvent<HTMLSpanElement>, id: string) => void;
}

const TireTable: React.FC<TireTableProps> = (props) => {
    const {
        tires, groups, allSameDimensions, dirtyFields,
        tireTypeOptions, manufacturerOptions,
        onGroupChange, onDeleteClick,
    } = props;

    const { t } = useTranslate();

    const dropdownProps = { tireTypeOptions, manufacturerOptions };

    return (
        <div className="bg-white rounded-sm border">
            <table className="min-w-full whitespace-nowrap divide-y divide-[#ebebeb] border-collapse p-0">
                <thead className="relative sticky top-0 z-10">
                    <tr>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]" colSpan={2}>Osovina / Guma</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.width")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.height")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.radius")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.manufacturer")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.tiretype")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.dot")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-left text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.profiledepth")}</th>
                        <th className="px-4 pt-3 pb-2 text-xs font-normal text-center text-slate-900 uppercase h-[48px]">{t("admin.fleet.tire.add.rof")}</th>
                        <th className="px-4 pt-3 pb-2 h-[48px]"></th>
                    </tr>
                </thead>
                <tbody className="bg-white w-full">
                    {allSameDimensions ? (
                        <>
                            <TireGroupHeader
                                label="Sve gume"
                                indices={tires.map((_, i) => i)}
                                sample={tires[0]}
                                onChange={onGroupChange}
                                {...dropdownProps}
                            />
                            {tires.map((item, i) => (
                                <TireReadOnlyRow
                                    key={i}
                                    item={item}
                                    index={i}
                                    label={getTireLabel(i, tires.length)}
                                    dirtyFields={dirtyFields}
                                    onDeleteClick={onDeleteClick}
                                />
                            ))}
                        </>
                    ) : (
                        <>
                            {(() => {
                                let globalIndex = 0;
                                return groups.map((group, gi) => {
                                    const startIndex = globalIndex;
                                    const rows = (
                                        <React.Fragment key={gi}>
                                            <TireGroupHeader
                                                label={getGroupLabel(gi, groups.length)}
                                                indices={group.indices}
                                                sample={group.tires[0]}
                                                onChange={onGroupChange}
                                                {...dropdownProps}
                                            />
                                            {group.tires.map((item, i) => (
                                                <TireReadOnlyRow
                                                    key={startIndex + i}
                                                    item={item}
                                                    index={startIndex + i}
                                                    label={getTireLabel(startIndex + i, tires.length)}
                                                    dirtyFields={dirtyFields}
                                                    onDeleteClick={onDeleteClick}
                                                />
                                            ))}
                                        </React.Fragment>
                                    );
                                    globalIndex += group.tires.length;
                                    return rows;
                                });
                            })()}
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default memo(TireTable);
